import { Directive, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { RenderJob, renderFrameVeiled, RenderVeil } from '../render-flow.model';
import { RenderFlowService } from '../render-flow.service';
import { renderSampleById, RenderSample } from '../render.data';

// What every step with a :modelId shares: the job from the URL, kept current, and the way
// out ("Try a different model"). The shell handles moving between steps.
@Directive()
export abstract class RenderStepBase implements OnInit, OnDestroy {
  public modelId = '';
  public job?: RenderJob;
  // The frames a paid model still shows with the preview's watermark: blurred (renderFrameVeiled).
  public veil: RenderVeil = { landscape: false, portrait: false };

  private jobSubscription?: Subscription;
  private paramsSubscription?: Subscription;

  protected constructor(
    protected readonly route: ActivatedRoute,
    protected readonly router: Router,
    protected readonly flow: RenderFlowService,
  ) {}

  ngOnInit(): void {
    this.paramsSubscription = this.route.paramMap.subscribe((params) => {
      const modelId = params.get('modelId') || '';
      if (modelId === this.modelId) {
        return;
      }
      this.modelId = modelId;
      this.jobSubscription?.unsubscribe();
      this.jobSubscription = this.flow.job$(modelId).subscribe((job) => {
        this.job = job;
        const landscape = renderFrameVeiled(job, 'landscape');
        const portrait = renderFrameVeiled(job, 'portrait');
        if (landscape !== this.veil.landscape || portrait !== this.veil.portrait) {
          this.veil = { landscape, portrait };
        }
        if (job) {
          this.onJob(job);
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.paramsSubscription?.unsubscribe();
    this.jobSubscription?.unsubscribe();
  }

  // Called with every job update; steps override what they need.
  protected onJob(job: RenderJob): void {
    void job;
  }

  // The sample behind the job: its own for a sample job, the stand-in for an upload.
  public get sample(): RenderSample | undefined {
    if (!this.job) {
      return undefined;
    }
    if (this.job.source.kind === 'sample') {
      return renderSampleById(this.job.source.sampleId);
    }
    return renderSampleById(this.job.standInSampleId);
  }

  public get isUpload(): boolean {
    return this.job?.source.kind === 'upload';
  }

  // A sample walked through: shown, never sold (owner, 2026-10-23).
  public get isSample(): boolean {
    return this.job?.source.kind === 'sample';
  }

  // "Sample model" / "lamborghini.fbx · 12 files · 45.2 MB"
  public get sourceLabel(): string {
    const source = this.job?.source;
    if (!source) {
      return '';
    }
    if (source.kind === 'sample') {
      return 'Sample model';
    }
    const files = source.fileCount === 1 ? 'single file' : `${source.fileCount} files`;
    return `${source.fileName} · ${files}`;
  }

  public onAnotherModel(): void {
    this.router.navigateByUrl('/render/upload');
  }
}
